// src/lib/api/user-videos.ts — video library (PRI-291 Phase 7)
// createUserVideo, getUserVideos, deleteUserVideo, saveUserVideoResult, sessions, etc.

// PRI-496 P0→Lane B: the user-videos console-persistence plane (library rows,
// thumbnails, session results with stream history/narrative JSON) is
// deliberately console-internal — routed through the chokepoint. Demo videos
// went PUBLIC in the 2026-07-31 batch and ride the public transport.
import { internalRequest as request } from '../internal-api-client'
import { request as publicRequest } from './client'

// ── Types ─────────────────────────────────────────────────────────────────────

export interface UserVideoResult {
  answer: string | null;
  confidence: number | null;
  query_type: string | null;
  result_json: Record<string, unknown> | null;
  job_id: string | null;
  video_url: string | null;
  computed_at: string;
}

export interface UserVideo {
  id: string;
  display_name: string;
  filename: string;
  file_size_bytes: number | null;
  duration_seconds: number | null;
  scan_id: string | null;
  source_s3_key: string | null;
  thumbnail_data_url: string | null;
  sort_order: number;
  created_at: string;
  /** PRI-496: public `videos` resource id (video_…) — dispatch public analyses re-runs. */
  public_id?: string | null;
  /** PRI-496: public video lifecycle status ('ready' required for analyses). */
  status?: string | null;
  results_by_prompt: Record<string, UserVideoResult>;
}

/** Typed structure for result_json stored in session history entries (Phase 8F, PRI-291) */
export interface SessionHistoryResultJson {
  streamHistory?: Array<{
    frame_num: number
    answer: string
    confidence: number
    elapsed_s?: number
    term_confidences?: Record<string, number>
    recording_elapsed_ms?: number
  }>
  narrative?: Array<{
    wall_clock: string
    elapsed_s: number
    window_start_elapsed_s: number
    recording_elapsed_ms?: number
    text: string
    type: 'detection' | 'absence' | 'action' | 'count' | 'summary' | 'status' | 'prompt_change'
  }>
  answer?: string | null
  confidence?: number | null
  [key: string]: unknown
}

export interface VideoSessionEntry {
  recording_s3_key?: string | null;
  id: string;
  prompt: string;
  answer: string;
  confidence: number | null;
  quality: string | null;
  query_type: string | null;
  result_json: SessionHistoryResultJson | null;
  video_url: string | null;
  job_id: string | null;
  created_at: string;
  narrative?: Array<{
    wall_clock: string;
    elapsed_s: number;
    window_start_elapsed_s: number;
    recording_elapsed_ms: number;
    text: string;
    type: string;
  }> | null;
  narrative_status?: string | null;
}

export interface DemoVideoResult {
  answer: string | null;
  confidence: number | null;
  query_type: string | null;
  result_json: Record<string, unknown> | null;
  video_url: string | null;
  computed_at: string;
}

export interface DemoVideo {
  id: string;
  display_name: string;
  filename: string;
  thumbnail_url: string | null;
  file_size_bytes: number | null;
  duration_seconds: number | null;
  sort_order: number;
  results_by_prompt: Record<string, DemoVideoResult>;
}

// ── Functions ─────────────────────────────────────────────────────────────────

export function getUserVideos(): Promise<{ videos: UserVideo[] }> {
  return request('/v1/user-videos');
}

export function createUserVideo(data: {
  filename: string; display_name?: string; file_size_bytes?: number;
  duration_seconds?: number; scan_id?: string; thumbnail_data_url?: string;
}): Promise<{ ok: boolean; video: UserVideo }> {
  return request('/v1/user-videos', { method: 'POST', body: JSON.stringify(data) });
}

export function updateUserVideo(id: string, data: {
  display_name?: string; scan_id?: string; thumbnail_data_url?: string; source_s3_key?: string;
}): Promise<{ ok: boolean; video: UserVideo }> {
  return request(`/v1/user-videos/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
}

export function deleteUserVideo(id: string): Promise<{ ok: boolean }> {
  return request(`/v1/user-videos/${id}`, { method: 'DELETE' });
}

/** Delete only the session results for a video (used for demo videos) */
export function deleteUserVideoSession(id: string): Promise<{ ok: boolean }> {
  return request(`/v1/user-videos/${id}/session`, { method: 'DELETE' });
}

/** Delete a single session result entry by its row id */
export function deleteSessionResult(videoId: string, resultId: string): Promise<{ ok: boolean }> {
  return request(`/v1/user-videos/${videoId}/session/${resultId}`, { method: 'DELETE' });
}

export function saveUserVideoResult(id: string, data: {
  prompt: string; job_id?: string | null; answer?: string | null;
  confidence?: number | null; query_type?: string | null;
  result_json?: Record<string, unknown> | null; video_path?: string | null; s3_key?: string | null;
}): Promise<{ ok: boolean }> {
  return request(`/v1/user-videos/${id}/results`, { method: 'PUT', body: JSON.stringify(data) });
}

export function getVideoSession(videoId: string): Promise<{ session: VideoSessionEntry[] }> {
  return request(`/v1/user-videos/${videoId}/session`);
}

export function saveVideoSessionEntry(videoId: string, data: {
  prompt: string;
  answer: string;
  confidence: number | null;
  quality: string | null;
  query_type: string | null;
  result_json: Record<string, unknown> | null;
  video_s3_key: string;
  job_id: string | null;
}): Promise<{ id: string; created_at: string }> {
  return request(`/v1/user-videos/${videoId}/session`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// PRI-496 Lane B: getSourceVideoUrl (internal /v1/user-videos/:id/source) is
// DELETED — zero consumers; source playback rides public Video.media
// (sign-on-read) via getPublicVideoMediaUrl in public-inference.ts.

// GET /v1/demo-videos is PUBLIC as of the 2026-07-31 contract batch (agent
// onboarding samples, documented subset). No auth required.
export function getDemoVideos(): Promise<{ videos: DemoVideo[] }> {
  return publicRequest('/v1/demo-videos');
}

export function attachRenderedVideoToSession(videoId: string, sessionId: string, s3Key: string): Promise<{ ok: boolean }> {
  return request(`/v1/user-videos/${videoId}/session/${sessionId}/attach-video`, {
    method: 'PATCH',
    body: JSON.stringify({ s3_key: s3Key }),
  });
}

// PRI-496 Lane B: getStreamRecordingPlaybackUrl + getVisionVideoPlaybackUrl
// are DELETED — zero consumers. Stream-recording playback migrates to public
// GET /v1/streams/{id}/recording with the streaming cutover (PRI-531 gate);
// annotated analysis playback rides Analysis.artifacts (public-inference.ts).
